<template>
    <div class="preview-panel">
        <h4>Template preview</h4>

        <!-- Call Permission Request GIF Preview -->
        <div v-if="isCallPermissionPreview" class="call-permission-preview">
            <img
                src="https://static.supsis.live/common/image/call_permission_request_template.gif"
                alt="Call Permission Request"
            />
        </div>

        <!-- Normal Preview -->
        <div v-else class="phone-preview">
            <div class="message-bubble">
                <!-- Step 0 Preview Templates -->
                <template v-if="currentStep === 0">
                    <!-- Marketing Default Preview -->
                    <template v-if="form.category === 'MARKETING' && form.templateType === 'DEFAULT'">
                        <div v-if="media && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerFormat)" class="header-preview">
                            <img v-if="form.headerFormat === 'IMAGE'" :src="media.data" alt="Preview" class="preview-image" />
                            <video v-else-if="form.headerFormat === 'VIDEO'" :src="media.data" class="preview-image" controls />
                            <div v-else-if="form.headerFormat === 'DOCUMENT'" class="document-preview">
                                <span class="material-icons">description</span>
                                <span>{{ media.name }}</span>
                            </div>
                        </div>
                        <div v-else-if="['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerFormat)" class="header-preview">
                            <img
                                src="https://static.supsis.live/common/image/fruits.png"
                                alt="Preview"
                                class="preview-image"
                            />
                        </div>
                        <div v-else-if="form.headerFormat === 'TEXT' && form.headerText" class="header-preview">
                            <div class="header-text-preview">{{ form.headerText }}</div>
                        </div>
                        <div class="body-preview">
                            {{ form.body || "Hey there! Check out our fresh groceries now! Use code HEALTH to get additional 10% off on your entire purchase." }}
                        </div>
                        <div class="footer-preview" v-if="form.footer">{{ form.footer }}</div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>

                    <!-- Marketing Carousel Preview -->
                    <template v-else-if="form.category === 'MARKETING' && form.templateType === 'CAROUSEL'">
                        <div class="body-preview">
                            Check out our featured products! Swipe to explore amazing deals.
                        </div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>

                    <!-- Marketing Limited Time Offer Preview -->
                    <template v-else-if="form.category === 'MARKETING' && form.templateType === 'LIMITED_TIME_OFFER'">
                        <div v-if="media && ['IMAGE', 'VIDEO'].includes(form.headerFormat)" class="header-preview">
                            <img v-if="form.headerFormat === 'IMAGE'" :src="media.data" alt="Preview" class="preview-image" />
                            <video v-else-if="form.headerFormat === 'VIDEO'" :src="media.data" class="preview-image" controls />
                        </div>
                        <div v-else-if="['IMAGE', 'VIDEO'].includes(form.headerFormat)" class="header-preview">
                            <img
                                src="https://static.supsis.live/common/image/fruits.png"
                                alt="Preview"
                                class="preview-image"
                            />
                        </div>
                        <div class="limited-time-offer-preview">
                            <div class="lto-icon">
                                <span class="material-icons">card_giftcard</span>
                            </div>
                            <div class="lto-content">
                                <div class="lto-title">{{ form.limitedTimeOffer.text || 'Special Offer' }}</div>
                                <div class="lto-expiry">Ends in 2 days</div>
                            </div>
                        </div>
                        <div class="body-preview">
                            {{ form.body || 'Starting now! Buy two, get one free on our new soap collection. Make this Black Friday unforgettable with artisanal, handmade soaps.' }}
                        </div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>

                    <!-- Marketing Coupon Code Preview -->
                    <template v-else-if="form.category === 'MARKETING' && form.templateType === 'COUPON_CODE'">
                        <div v-if="form.headerFormat === 'TEXT' && form.headerText" class="header-preview">
                            <div class="header-text-preview">{{ form.headerText }}</div>
                        </div>
                        <div v-else-if="form.headerFormat === 'TEXT'" class="header-preview">
                            <div class="header-text-preview">Our Winter Sale is on!</div>
                        </div>
                        <div class="body-preview">
                            {{ form.body || "Shop now through the end of December and use the one-time use code WINTER25 to get 25% off of your entire order!" }}
                        </div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>

                    <!-- Marketing Catalogue Preview -->
                    <template v-else-if="form.category === 'MARKETING' && form.templateType === 'CATALOGUE'">
                        <div class="catalogue-card-preview">
                            <div class="catalogue-image-placeholder">
                                <span class="material-icons">image</span>
                            </div>
                            <div class="catalogue-info">
                                <div class="catalogue-title">View {BIZ_NAME}'s Catalog on WhatsApp</div>
                                <div class="catalogue-subtitle">Browse pictures and details of their offerings.</div>
                            </div>
                        </div>
                        <div class="body-preview">
                            <template v-if="form.catalogueSubType === 'CATALOGUE'">
                                Now shop for your favorite products right here on WhatsApp! Browse our full catalogue.
                            </template>
                            <template v-else-if="form.catalogueSubType === 'SINGLE_PRODUCT'">
                                Check out this amazing product! Perfect for your needs.
                            </template>
                            <template v-else-if="form.catalogueSubType === 'MULTI_PRODUCT'">
                                Forget something? Looks like you left these items in your cart.
                            </template>
                            <template v-else-if="form.catalogueSubType === 'PRODUCT_CARD_CAROUSEL'">
                                Discover our latest products! Browse our catalogue and find exactly what you're looking for.
                            </template>
                        </div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>

                    <!-- Utility Default Preview -->
                    <template v-else-if="form.category === 'UTILITY' && form.templateType === 'DEFAULT'">
                        <div v-if="media && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerFormat)" class="header-preview">
                            <img v-if="form.headerFormat === 'IMAGE'" :src="media.data" alt="Preview" class="preview-image" />
                            <video v-else-if="form.headerFormat === 'VIDEO'" :src="media.data" class="preview-image" controls />
                            <div v-else-if="form.headerFormat === 'DOCUMENT'" class="document-preview">
                                <span class="material-icons">description</span>
                                <span>{{ media.name }}</span>
                            </div>
                        </div>
                        <div v-else-if="form.headerFormat === 'TEXT' && form.headerText" class="header-preview">
                            <div class="header-text-preview">{{ form.headerText }}</div>
                        </div>
                        <div class="body-preview">
                            {{ form.body || "Good news! Your order 23KFEJJ2312 has shipped! Here's your tracking information, please check link below." }}
                        </div>
                        <div class="footer-preview" v-if="form.footer">{{ form.footer }}</div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>

                    <!-- Authentication OTP Preview -->
                    <template v-else-if="form.category === 'AUTHENTICATION'">
                        <div v-if="media && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerFormat)" class="header-preview">
                            <img v-if="form.headerFormat === 'IMAGE'" :src="media.data" alt="Preview" class="preview-image" />
                            <video v-else-if="form.headerFormat === 'VIDEO'" :src="media.data" class="preview-image" controls />
                            <div v-else-if="form.headerFormat === 'DOCUMENT'" class="document-preview">
                                <span class="material-icons">description</span>
                                <span>{{ media.name }}</span>
                            </div>
                        </div>
                        <div v-else-if="form.headerFormat === 'TEXT' && form.headerText" class="header-preview">
                            <div class="header-text-preview">{{ form.headerText }}</div>
                        </div>
                        <div class="body-preview">
                            {{ form.body || "123456 is your verification code. For your security, do not share this code." }}
                        </div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>
                </template>

                <!-- Step 1+ User Input Preview -->
                <template v-else>
                    <!-- Carousel Preview (no header/footer) -->
                    <template v-if="form.templateType === 'CAROUSEL'">
                        <div class="body-preview">{{ form.body || "Your carousel message body will appear here" }}</div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>

                    <!-- Limited Time Offer Preview -->
                    <template v-else-if="form.templateType === 'LIMITED_TIME_OFFER'">
                        <div v-if="media" class="header-preview">
                            <img v-if="form.headerFormat === 'IMAGE'" :src="media.data" alt="Preview" class="preview-image" />
                            <video v-else-if="form.headerFormat === 'VIDEO'" :src="media.data" class="preview-image" />
                        </div>
                        <div v-else-if="form.headerFormat === 'IMAGE' || form.headerFormat === 'VIDEO'" class="header-preview">
                            <div class="media-placeholder" :class="form.headerFormat.toLowerCase()">
                                <span class="material-icons">{{ form.headerFormat === 'IMAGE' ? 'image' : 'play_circle_filled' }}</span>
                            </div>
                        </div>
                        <div class="limited-time-offer-preview" v-if="form.limitedTimeOffer && form.limitedTimeOffer.text">
                            <div class="lto-icon">
                                <span class="material-icons">card_giftcard</span>
                            </div>
                            <div class="lto-content">
                                <div class="lto-title">{{ form.limitedTimeOffer.text }}</div>
                                <div class="lto-expiry" v-if="form.limitedTimeOffer.hasExpiration">
                                    Ends in 2 days
                                </div>
                            </div>
                        </div>
                        <div class="body-preview">{{ form.body || "Your message body will appear here" }}</div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>

                    <!-- Coupon Code Preview (Step 1+) -->
                    <template v-else-if="form.templateType === 'COUPON_CODE'">
                        <div v-if="form.headerFormat === 'TEXT' && form.headerText" class="header-preview">
                            <div class="header-text-preview">{{ form.headerText }}</div>
                        </div>
                        <div class="body-preview">{{ form.body || "Your message body will appear here" }}</div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>

                    <!-- Catalogue Preview (Step 1+) -->
                    <template v-else-if="form.templateType === 'CATALOGUE'">
                        <div class="catalogue-card-preview">
                            <div class="catalogue-image-placeholder">
                                <span class="material-icons">image</span>
                            </div>
                            <div class="catalogue-info">
                                <div class="catalogue-title">View {BIZ_NAME}'s Catalog on WhatsApp</div>
                                <div class="catalogue-subtitle">Browse pictures and details of their offerings.</div>
                            </div>
                        </div>
                        <div class="body-preview">{{ form.body || "Your message body will appear here" }}</div>
                        <div class="footer-preview" v-if="form.footer">{{ form.footer }}</div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>

                    <!-- Call Permission Preview (Step 1+) -->
                    <template v-else-if="form.templateType === 'CALL_PERMISSION'">
                        <div v-if="form.headerFormat === 'TEXT' && form.headerText" class="header-preview">
                            <div class="header-text-preview">{{ form.headerText }}</div>
                        </div>
                        <div class="body-preview">{{ form.body || "Your message body will appear here" }}</div>
                        <div class="footer-preview" v-if="form.footer">{{ form.footer }}</div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>

                    <!-- Utility Preview (Step 1+) -->
                    <template v-else-if="form.category === 'UTILITY'">
                        <div class="header-preview" v-if="form.headerFormat !== 'NONE'">
                            <!-- Uploaded Media -->
                            <template v-if="media">
                                <img v-if="form.headerFormat === 'IMAGE'" :src="media.data" alt="Preview" class="preview-image" />
                                <video v-else-if="form.headerFormat === 'VIDEO'" :src="media.data" class="preview-image" controls />
                                <div v-else-if="form.headerFormat === 'DOCUMENT'" class="document-preview">
                                    <span class="material-icons">description</span>
                                    <span>{{ media.name }}</span>
                                </div>
                            </template>
                            <!-- Media Placeholders -->
                            <template v-else-if="['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerFormat)">
                                <div v-if="form.headerFormat === 'IMAGE'" class="media-placeholder image">
                                    <span class="material-icons">image</span>
                                </div>
                                <div v-else-if="form.headerFormat === 'VIDEO'" class="media-placeholder video">
                                    <span class="material-icons">play_circle_filled</span>
                                </div>
                                <div v-else-if="form.headerFormat === 'DOCUMENT'" class="media-placeholder document">
                                    <span class="material-icons">description</span>
                                </div>
                            </template>
                            <!-- Header Text -->
                            <div v-if="form.headerFormat === 'TEXT' && form.headerText" class="header-text-preview">{{ form.headerText }}</div>
                        </div>
                        <div class="body-preview">{{ form.body || "Your message body will appear here" }}</div>
                        <div class="footer-preview" v-if="form.footer">{{ form.footer }}</div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>

                    <!-- Authentication Preview (Step 1+) -->
                    <template v-else-if="form.category === 'AUTHENTICATION'">
                        <div class="header-preview" v-if="form.headerFormat !== 'NONE'">
                            <!-- Uploaded Media -->
                            <template v-if="media">
                                <img v-if="form.headerFormat === 'IMAGE'" :src="media.data" alt="Preview" class="preview-image" />
                                <video v-else-if="form.headerFormat === 'VIDEO'" :src="media.data" class="preview-image" controls />
                                <div v-else-if="form.headerFormat === 'DOCUMENT'" class="document-preview">
                                    <span class="material-icons">description</span>
                                    <span>{{ media.name }}</span>
                                </div>
                            </template>
                            <!-- Media Placeholders -->
                            <template v-else-if="['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerFormat)">
                                <div v-if="form.headerFormat === 'IMAGE'" class="media-placeholder image">
                                    <span class="material-icons">image</span>
                                </div>
                                <div v-else-if="form.headerFormat === 'VIDEO'" class="media-placeholder video">
                                    <span class="material-icons">play_circle_filled</span>
                                </div>
                                <div v-else-if="form.headerFormat === 'DOCUMENT'" class="media-placeholder document">
                                    <span class="material-icons">description</span>
                                </div>
                            </template>
                            <!-- Header Text -->
                            <div v-if="form.headerFormat === 'TEXT' && form.headerText" class="header-text-preview">{{ form.headerText }}</div>
                        </div>
                        <div class="body-preview">{{ form.body || "Your message body will appear here" }}</div>
                        <div class="footer-preview" v-if="form.footer">{{ form.footer }}</div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>

                    <!-- Default Preview -->
                    <template v-else>
                        <div class="header-preview" v-if="form.headerFormat !== 'NONE'">
                            <!-- Uploaded Media -->
                            <template v-if="media">
                                <img v-if="form.headerFormat === 'IMAGE'" :src="media.data" alt="Preview" class="preview-image" />
                                <video v-else-if="form.headerFormat === 'VIDEO'" :src="media.data" class="preview-image" controls />
                                <div v-else-if="form.headerFormat === 'DOCUMENT'" class="document-preview">
                                    <span class="material-icons">description</span>
                                    <span>{{ media.name }}</span>
                                </div>
                            </template>
                            <!-- Media Placeholders -->
                            <template v-else>
                                <div v-if="form.headerFormat === 'IMAGE'" class="media-placeholder image">
                                    <span class="material-icons">image</span>
                                </div>
                                <div v-else-if="form.headerFormat === 'VIDEO'" class="media-placeholder video">
                                    <span class="material-icons">play_circle_filled</span>
                                </div>
                                <div v-else-if="form.headerFormat === 'DOCUMENT'" class="media-placeholder document">
                                    <span class="material-icons">description</span>
                                </div>
                            </template>
                            <!-- Header Text -->
                            <div v-if="form.headerFormat === 'TEXT' && form.headerText" class="header-text">{{ form.headerText }}</div>
                        </div>
                        <div class="body-preview">{{ form.body || "Your message body will appear here" }}</div>
                        <div class="footer-preview" v-if="form.footer">{{ form.footer }}</div>
                        <div class="time-stamp">{{ getCurrentTime }}</div>
                    </template>
                </template>
            </div>

            <!-- Buttons Preview -->
            <div
                class="buttons-preview"
                v-if="currentStep === 0 && form.category === 'MARKETING' && (form.templateType === 'DEFAULT' || form.templateType === 'CAROUSEL' || form.templateType === 'LIMITED_TIME_OFFER' || form.templateType === 'COUPON_CODE')"
            >
                <template v-if="form.templateType === 'CAROUSEL'">
                    <div class="preview-button"><a-icon type="link" /> View Product</div>
                    <div class="preview-button"><a-icon type="phone" /> Call Us</div>
                </template>
                <template v-else-if="form.templateType === 'LIMITED_TIME_OFFER'">
                    <div class="preview-button"><a-icon type="copy" /> Copy offer code</div>
                    <div class="preview-button"><a-icon type="link" /> Shop now</div>
                </template>
                <template v-else-if="form.templateType === 'COUPON_CODE'">
                    <div class="preview-button"><a-icon type="message" /> Unsubscribe</div>
                    <div class="preview-button-copy-code"><a-icon type="copy" /> Copy code</div>
                </template>
                <template v-else>
                    <div class="preview-button"><a-icon type="link" /> Shop now</div>
                    <div class="preview-button"><a-icon type="copy" /> Copy code</div>
                </template>
            </div>
            <div
                class="buttons-preview"
                v-else-if="currentStep === 0 && form.category === 'MARKETING' && form.templateType === 'CATALOGUE'"
            >
                <template v-if="form.catalogueSubType === 'CATALOGUE'">
                    <div class="preview-button"><a-icon type="shopping" /> View catalog</div>
                </template>
                <template v-else-if="form.catalogueSubType === 'SINGLE_PRODUCT'">
                    <div class="preview-button"><a-icon type="shopping" /> View</div>
                </template>
                <template v-else-if="form.catalogueSubType === 'MULTI_PRODUCT'">
                    <div class="preview-button"><a-icon type="shopping-cart" /> View items</div>
                </template>
                <template v-else-if="form.catalogueSubType === 'PRODUCT_CARD_CAROUSEL'">
                    <div class="preview-button"><a-icon type="shopping" /> View</div>
                </template>
            </div>
            <div
                class="buttons-preview"
                v-else-if="currentStep === 0 && form.category === 'UTILITY' && form.templateType === 'DEFAULT'"
            >
                <div class="preview-button"><a-icon type="link" /> Track shipment</div>
            </div>
            <div
                class="buttons-preview"
                v-else-if="currentStep === 0 && form.templateType === 'CALL_PERMISSION'"
            >
                <div class="preview-button-call-permission">
                    Choose preference <a-icon type="down" />
                </div>
            </div>
            <!-- Carousel card buttons are shown inside the cards themselves, not here -->
            <div class="buttons-preview" v-else-if="currentStep === 0 && form.category === 'AUTHENTICATION'">
                <div class="preview-button">
                    <a-icon :type="form.authentication.otpSubType === 'COPY_CODE' ? 'copy' : 'mobile'" />
                    {{ form.authentication.otpSubType === 'COPY_CODE' ? 'Copy code' : 
                       form.authentication.otpSubType === 'ONE_TAP' ? 'Autofill' : 
                       'Auto-verify' }}
                </div>
            </div>
            <div class="buttons-preview" v-else-if="currentStep > 0 && form.templateType === 'CALL_PERMISSION'">
                <div class="preview-button-call-permission">
                    Choose preference <a-icon type="down" />
                </div>
            </div>
            <div class="buttons-preview" v-else-if="currentStep > 0 && form.templateType !== 'CAROUSEL' && form.buttons && form.buttons.length">
                <div v-for="(button, index) of form.buttons" :key="index" class="preview-button">
                    <a-icon v-if="button.type === 'URL'" type="link" />
                    <a-icon v-if="button.type === 'PHONE_NUMBER'" type="phone" />
                    <a-icon v-if="button.type === 'MPM'" type="shopping-cart" />
                    <a-icon v-if="button.type === 'SPM'" type="shopping" />
                    <a-icon v-if="button.type === 'COPY_CODE'" type="copy" />
                    <a-icon v-if="button.type === 'OTP'" type="mobile" />
                    {{ button.text || "Button " + (index + 1) }}
                </div>
            </div>
            
            <!-- Carousel Cards - Outside message-bubble -->
            <template v-if="form.templateType === 'CAROUSEL'">
                <!-- Step 0: Sample carousel cards -->
                <div class="carousel-whatsapp-message" v-if="currentStep === 0">
                    <div class="carousel-whatsapp-message-item" v-for="n in 3" :key="n">
                        <div class="content-wrapper">
                            <img src="https://static.supsis.live/common/image/fruits.png" alt="Sample Card" />
                            <div class="body">Product {{ n }} description</div>
                        </div>
                        <div class="buttons">
                            <div class="button url">
                                <a-icon type="link" /> Shop now
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Step 1+: Real carousel cards -->
                <div class="carousel-whatsapp-message" v-else-if="form.carouselCards && form.carouselCards.length > 0">
                    <div class="carousel-whatsapp-message-item" v-for="(card, index) in form.carouselCards" :key="index">
                        <div class="content-wrapper">
                            <div v-if="card.media" class="card-media">
                                <img v-if="card.headerFormat === 'IMAGE'" :src="card.media.data" alt="Card Preview" />
                                <video v-else-if="card.headerFormat === 'VIDEO'" :src="card.media.data" controls />
                            </div>
                            <div v-else class="media-placeholder" :class="card.headerFormat.toLowerCase()">
                                <span class="material-icons">{{ card.headerFormat === 'IMAGE' ? 'image' : 'play_circle_filled' }}</span>
                            </div>
                            <div class="body" v-if="card.body">{{ card.body }}</div>
                        </div>
                        <div class="buttons" v-if="card.buttons && card.buttons.length">
                            <div v-for="(btn, btnIdx) in card.buttons" :key="btnIdx" class="button" :class="btn.type === 'QUICK_REPLY' ? 'quick-reply' : 'url'">
                                <a-icon v-if="btn.type === 'URL'" type="link" />
                                <a-icon v-if="btn.type === 'PHONE_NUMBER'" type="phone" />
                                {{ btn.text || getButtonLabel(btn.type) }}
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <div class="preview-info">
            <h5>This template is good for</h5>
            <p>{{ getPreviewDescription }}</p>

            <template v-if="currentStep === 0">
                <h5>Template areas that you can customise</h5>
                <p>{{ getCustomisableAreas }}</p>
            </template>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";

export default Vue.extend({
    name: "TemplatePreview",
    props: {
        form: { type: Object, required: true },
        currentStep: { type: Number, required: true },
        media: { type: Object, default: null },
        headerVariables: { type: Array, default: () => [] },
        bodyVariables: { type: Array, default: () => [] },
    },
    computed: {
        isCallPermissionPreview() {
            return this.form.templateType === "CALL_PERMISSION" && this.currentStep === 0;
        },
        getCurrentTime() {
            return moment().format("HH:mm");
        },
        getPreviewDescription() {
            if (this.form.category === "MARKETING") {
                if (this.form.templateType === "CATALOGUE") {
                    return "Product/service discovery, sales, offers, targeted marketing, personalised shopping";
                }
                if (this.form.templateType === "COUPON_CODE") {
                    return "Send promotional codes with a copy button for easy redemption. Limited to one copy code button.";
                }
                if (this.form.templateType === "CAROUSEL") {
                    return "Showcase multiple products or services in a horizontally scrollable view. Each card can have its own image, description, and call-to-action buttons.";
                }
                return "Welcome messages, promotions, offers, coupons, newsletters, announcements";
            }
            if (this.form.category === "UTILITY") {
                return "Order confirmations, account update, receipts, appointment reminders, billing";
            }
            if (this.form.category === "AUTHENTICATION") {
                return "One-time password, account recovery code, account verification, integrity challenges";
            }
            return "";
        },
        getCustomisableAreas() {
            if (this.form.category === "MARKETING") {
                if (this.form.templateType === "CALL_PERMISSION") {
                    return "Header, body, footer";
                }
                if (this.form.templateType === "COUPON_CODE") {
                    return "Header (text), body, button (except copy code text)";
                }
                if (this.form.templateType === "CAROUSEL") {
                    return "General body message, carousel cards (each with media, body, buttons)";
                }
                return "Media, header, body, footer, button";
            }
            if (this.form.category === "UTILITY") {
                return "Header, body, footer";
            }
            if (this.form.category === "AUTHENTICATION") {
                return "Body";
            }
            return "";
        },
        getCatalogueSubTypeLabel() {
            const labels = {
                CATALOGUE: "Catalogue Message",
                SINGLE_PRODUCT: "Single Product",
                MULTI_PRODUCT: "Multi Product",
                PRODUCT_CARD_CAROUSEL: "Product Card Carousel",
            };
            return labels[this.form.catalogueSubType] || "Product Message";
        },
    },
    methods: {
        getButtonIcon(type) {
            const icons = {
                'QUICK_REPLY': 'message',
                'URL': 'link',
                'PHONE_NUMBER': 'phone',
                'COPY_CODE': 'copy',
            };
            return icons[type] || 'link';
        },
        getButtonLabel(type) {
            const labels = {
                'QUICK_REPLY': 'Quick Reply',
                'URL': 'Visit Website',
                'PHONE_NUMBER': 'Call Phone',
                'COPY_CODE': 'Copy Code',
            };
            return labels[type] || type;
        },
    },
});
</script>

<style scoped lang="scss">
.preview-panel {
    width: 400px;
    min-width: 400px;
    background: white;
    border-left: 1px solid #e8e8e8;
    padding: 24px;
    overflow-y: auto;
    flex-shrink: 0;

    h4 {
        margin: 0 0 20px 0;
        font-weight: 600;
        font-size: 16px;
    }

    .call-permission-preview {
        background: linear-gradient(180deg, #e5ddd5 0%, #d5d0c9 100%);
        border-radius: 8px;
        padding: 20px;
        min-height: 400px;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
            max-width: 100%;
            border-radius: 8px;
        }
    }

    .phone-preview {
        background: linear-gradient(180deg, #e5ddd5 0%, #d5d0c9 100%);
        border-radius: 8px;
        padding: 20px;
        min-height: 300px;

        .message-bubble {
            background: white;
            border-radius: 8px;
            padding: 12px;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
            position: relative;

            .header-preview {
                margin-bottom: 8px;

                .preview-image {
                    width: 100%;
                    border-radius: 4px;
                    max-height: 180px;
                    object-fit: cover;
                }

                video.preview-image {
                    max-height: 180px;
                    background: #000;
                }

                .document-preview {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 16px;
                    background: #f5f5f5;
                    border-radius: 4px;
                    border: 1px solid #e0e0e0;

                    .material-icons {
                        font-size: 32px;
                        color: #1890ff;
                    }

                    span:not(.material-icons) {
                        font-size: 14px;
                        color: #333;
                        font-weight: 500;
                    }
                }

                .media-placeholder {
                    background: #e0e0e0;
                    border-radius: 4px;
                    height: 120px;
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    .material-icons {
                        font-size: 48px;
                        color: #999;
                    }

                    &.video {
                        background: #333;
                        .material-icons {
                            color: white;
                        }
                    }
                }

                .header-text {
                    font-weight: 600;
                }

                .header-text-preview {
                    font-weight: 600;
                    font-size: 15px;
                    color: #1a1a1a;
                    margin-bottom: 8px;
                }

                .header-text {
                    font-weight: 600;
                    font-size: 15px;
                    color: #1a1a1a;
                    margin-bottom: 8px;
                }
            }

            .body-preview {
                font-size: 14px;
                color: #1a1a1a;
                word-break: break-word;
                line-height: 1.5;
            }

            .footer-preview {
                font-size: 12px;
                color: #666;
                margin-top: 8px;
            }

            .time-stamp {
                font-size: 11px;
                color: #999;
                text-align: right;
                margin-top: 4px;
            }

            .catalogue-card-preview {
                margin-bottom: 12px;
                padding: 12px;
                background: #f5f5f5;
                border-radius: 8px;
                display: flex;
                gap: 12px;
                align-items: center;
                border: 1px solid #e0e0e0;

                .catalogue-image-placeholder {
                    width: 64px;
                    height: 64px;
                    background: #d9d9d9;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;

                    .material-icons {
                        font-size: 32px;
                        color: #999;
                    }
                }

                .catalogue-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    min-width: 0;
                    overflow: hidden;

                    .catalogue-title {
                        font-weight: 600;
                        font-size: 14px;
                        color: #262626;
                        line-height: 1.3;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        max-width: 100%;
                    }

                    .catalogue-subtitle {
                        font-size: 13px;
                        color: #595959;
                        line-height: 1.4;
                        overflow: hidden;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                    }
                }
            }

            .limited-time-offer-preview {
                margin: 12px 0;
                padding: 14px 16px;
                background: #f5f5f5;
                border-radius: 8px;
                display: flex;
                gap: 12px;
                align-items: center;
                border: 1px solid #e0e0e0;
                
                .lto-icon {
                    width: 48px;
                    height: 48px;
                    background: linear-gradient(135deg, #95de64 0%, #73d13d 100%);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;

                    .material-icons {
                        font-size: 24px;
                        color: white;
                    }
                }

                .lto-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 2px;

                    .lto-title {
                        font-weight: 600;
                        font-size: 15px;
                        color: #262626;
                        line-height: 1.3;
                    }

                    .lto-expiry {
                        font-size: 13px;
                        color: #595959;
                        font-weight: 400;
                    }
                }
            }

            // Eski carousel preview - step 0 için (basit gösterim)
            .carousel-preview {
                position: relative;

                .carousel-card {
                    position: relative;
                    border-radius: 8px;
                    overflow: hidden;
                    
                    .preview-image {
                        width: 100%;
                        border-radius: 4px;
                        max-height: 180px;
                        object-fit: cover;
                    }
                }

                .carousel-indicator {
                    position: absolute;
                    bottom: 8px;
                    right: 8px;
                    background: rgba(0, 0, 0, 0.6);
                    color: white;
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 11px;
                    font-weight: 500;
                }
            }

        }

        // Carousel cards - Outside message-bubble (like WhatsappMessagePreview.vue)
        .carousel-whatsapp-message {
            display: flex;
            gap: 10px;
            width: 100%;
            overflow-x: auto;
            margin-top: 8px;

            &::-webkit-scrollbar {
                height: 6px;
            }

            &::-webkit-scrollbar-track {
                background: #f1f1f1;
                border-radius: 3px;
            }

            &::-webkit-scrollbar-thumb {
                background: #888;
                border-radius: 3px;

                &:hover {
                    background: #555;
                }
            }

            .carousel-whatsapp-message-item {
                min-width: 260px;
                width: 260px;
                align-self: flex-start;

                .content-wrapper {
                    background-color: #fff;
                    border-radius: 4px;
                    padding: 8px;

                    img, video {
                        width: 100%;
                        border-radius: 4px;
                        max-height: 180px;
                        object-fit: cover;
                    }

                    .media-placeholder {
                        height: 180px;
                        background: #e0e0e0;
                        border-radius: 4px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-bottom: 8px;

                        &.image {
                            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        }

                        &.video {
                            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
                        }

                        .material-icons {
                            font-size: 48px;
                            color: white;
                        }
                    }

                    .body {
                        font-size: 14px;
                        font-weight: 400;
                        color: rgb(63, 63, 63);
                        overflow-wrap: break-word;
                        word-break: break-word;
                        padding: 8px 8px 6px;
                        white-space: pre-line;
                    }
                }

                .buttons {
                    display: flex;
                    flex-direction: column;
                    gap: 0px;

                    .button {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background-color: rgb(254, 254, 254);
                        margin: 8px 0px;
                        padding: 8px 16px;
                        text-align: center;
                        color: rgb(0, 165, 244);
                        cursor: pointer;
                        flex-grow: 1;
                        border-radius: 6px;
                        box-shadow: rgb(175, 183, 191) 0px 1px 3px 0px;
                        word-break: break-word;

                        &.url {
                            gap: 10px;

                            .anticon {
                                font-size: 16px;
                            }
                        }

                        &.quick-reply {
                            background: #f0f0f0;
                        }
                    }
                }
            }
        }

        .buttons-preview {
            margin-top: 8px;

            .preview-button {
                background: white;
                border-radius: 8px;
                padding: 12px;
                text-align: center;
                margin-top: 4px;
                color: #1890ff;
                font-weight: 500;
                font-size: 14px;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

                .anticon {
                    margin-right: 6px;
                }
            }

            .preview-button-copy-code {
                background: #e8e8e8;
                border-radius: 8px;
                padding: 12px;
                text-align: center;
                margin-top: 4px;
                color: #333;
                font-weight: 600;
                font-size: 14px;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
                border: 1px solid #d0d0d0;

                .anticon {
                    margin-right: 6px;
                }
            }

            .preview-button-call-permission {
                background: white;
                border-radius: 8px;
                padding: 12px;
                text-align: center;
                margin-top: 4px;
                color: #1890ff;
                font-weight: 500;
                font-size: 14px;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
                border: 1px solid #1890ff;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;

                .anticon {
                    font-size: 16px;
                }
            }
        }
    }

    .preview-info {
        margin-top: 24px;
        padding-top: 20px;
        border-top: 1px solid #e8e8e8;

        h5 {
            font-weight: 600;
            margin-bottom: 8px;
            font-size: 14px;
        }

        p {
            font-size: 13px;
            color: #666;
            margin-bottom: 16px;
        }
    }
}
</style>
